import React from 'react';
import { LandingComponent } from 'app/types';

import Select from 'react-select';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { useFormik } from 'formik';
import PhoneInput from 'react-phone-input-2';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

import styles from '../LandingForm.module.scss';
import { RESIDENT_COUNTRIES } from 'common/constants';
import { useSubmitGenericFormMutation } from 'services/exploreApi';

interface PreviewComponentProps {
  landing_cmp: LandingComponent;
}

interface FormData {
  First_Name: string;
  Last_Name: string;
  Email: string;
  Phone: string;
  PhoneConfirmation: string;
  Country_of_Residence: string;
  Preferred_Program_Level: string;
  Preferred_Intake_Year: string;
  Associated_List: string | undefined;
  Term_Policy: boolean;
}

const residentOptions = RESIDENT_COUNTRIES.map((v: string, _: number) => ({ label: v, value: v }));
const degreeTypeOptions = [
  { label: 'Bachelor', value: 'Bachelor' },
  { label: 'Master', value: 'Master' },
  { label: 'MBA', value: 'MBA' },
  { label: 'PhD', value: 'PhD' },
];

const preferredIntakeYearOptions = [
  { label: '2024', value: '2024' },
  { label: '2025', value: '2025' },
  { label: '2026 or later', value: '2026 or later' },
];

const FormSchema = Yup.object().shape({
  First_Name: Yup.string().min(2, 'Too Short!').max(70, 'Too Long!').required('Required'),
  Last_Name: Yup.string().min(2, 'Too Short!').required('Required'),
  Email: Yup.string().email('Invalid email').required('Required'),
  Phone: Yup.string().min(10, 'Too Short!').required('Required'),
  PhoneConfirmation: Yup.string()
    .min(10, 'Too Short!')
    .required('Required')
    .oneOf([Yup.ref('Phone')], 'Your values does not match'),
  Country_of_Residence: Yup.string().required('Required'),
  Preferred_Program_Level: Yup.string().required('Required'),
  Preferred_Intake_Year: Yup.string().required('Required'),
  Term_Policy: Yup.boolean()
    .oneOf([true], 'You must accept the terms and policy')
    .required('You must accept the terms and policy'),
});

const GeneralFormPreview = ({ landing_cmp }: PreviewComponentProps): React.ReactElement => {
  const [submitForm, { isLoading }] = useSubmitGenericFormMutation();
  const navigate = useNavigate();

  const formik = useFormik<FormData>({
    initialValues: {
      First_Name: '',
      Last_Name: '',
      Email: '',
      Phone: '',
      Country_of_Residence: '',
      PhoneConfirmation: '',
      Preferred_Program_Level: '',
      Preferred_Intake_Year: '',
      Associated_List: landing_cmp.config.associated_list,
      Term_Policy: false,
    },
    validationSchema: FormSchema,
    onSubmit: async (formData: FormData) => {
      const data = {
        ...formData,
        phone: formatPhoneNumberIntl('+' + formik.values.Phone),
        utm_medium: Cookies.get('__lt_utm_medium'),
        utm_source: Cookies.get('__lt_utm_source'),
        utm_term: Cookies.get('__lt_utm_term'),
        utm_campaign: Cookies.get('__lt_utm_campaign'),
        utm_content: Cookies.get('__lt_utm_content'),
      };

      submitForm({
        data: data,
      }).then(() => {
        window.location.href = landing_cmp.config.redirect_url;
      });
    },
  });

  return (
    <div className="container pt-5 mb-5 pb-5">
      <div className="row">
        <div className="col-12 col-md-6 order-md-1 order-2">
          <h2 className="mt-4 mb-3">{landing_cmp.config.heading}</h2>
          <p> {landing_cmp.config.sub_heading}</p>
          <form onSubmit={formik.handleSubmit} className="mt-5">
            <div>
              <label htmlFor="First_Name" className={`form-label ${styles.formLabel}`}>
                First Name *
              </label>
              <input
                name="First_Name"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.First_Name}
                className={`form-control ${styles.formInput}`}
              />
              {formik.touched.First_Name && formik.errors.First_Name ? (
                <div className="text-danger">{formik.errors.First_Name}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Last_Name" className={`form-label ${styles.formLabel}`}>
                Last Name *
              </label>
              <input
                name="Last_Name"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.Last_Name}
                className={`form-control ${styles.formInput}`}
              />
              {formik.touched.Last_Name && formik.errors.Last_Name ? (
                <div className="text-danger">{formik.errors.Last_Name}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Email" className={`form-label ${styles.formLabel}`}>
                Contact Email *
              </label>
              <input
                name="Email"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.Email}
                className={`form-control ${styles.formInput}`}
              />
              {formik.touched.Email && formik.errors.Email ? (
                <div className="text-danger">{formik.errors.Email}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Phone" className={`form-label ${styles.formLabel}`}>
                Whatsapp Phone *
              </label>
              <PhoneInput
                inputClass={`form-control ${styles.formInput}`}
                country={'gb'}
                countryCodeEditable={false}
                onChange={(e) => {
                  formik.setFieldValue('Phone', e);
                }}
                onBlur={formik.handleBlur}
                inputProps={{
                  name: 'Phone',
                }}
              />
              {formik.touched.Phone && formik.errors.Phone ? (
                <div className="text-danger">{formik.errors.Phone}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Phone" className={`form-label ${styles.formLabel}`}>
                Whatsapp Phone Confirmation *
              </label>
              <PhoneInput
                inputClass={`form-control ${styles.formInput}`}
                country={'gb'}
                countryCodeEditable={false}
                onChange={(e) => {
                  formik.setFieldValue('PhoneConfirmation', e);
                }}
                onBlur={formik.handleBlur}
                inputProps={{
                  name: 'PhoneConfirmation',
                }}
              />
              {formik.touched.PhoneConfirmation && formik.errors.PhoneConfirmation ? (
                <div className="text-danger">{formik.errors.PhoneConfirmation}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Country_of_Residence" className={`form-label ${styles.formLabel}`}>
                Country of Residence *
              </label>
              <Select
                isSearchable
                classNamePrefix="react-select"
                className={`${styles.formSelect} p-0`}
                name="Country_of_Residence"
                options={residentOptions}
                onChange={(e) => formik.setFieldValue('Country_of_Residence', e?.label)}
                onBlur={() => formik.setFieldTouched('Country_of_Residence', true)}
                styles={{
                  control: (baseStyles: any) => ({
                    ...baseStyles,
                    height: '100%',
                    width: '100%',
                    borderRadius: '8px',
                    border: 'none',
                  }),
                }}
              />
              {formik.touched.Country_of_Residence && formik.errors.Country_of_Residence ? (
                <div className="text-danger">{formik.errors.Country_of_Residence}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Preferred_Program_Level" className={`form-label ${styles.formLabel}`}>
                Which degree type do you want to study? *
              </label>
              <Select
                isSearchable
                classNamePrefix="react-select"
                className={`${styles.formSelect} p-0`}
                options={degreeTypeOptions}
                name="Preferred_Program_Level"
                onChange={(e) => formik.setFieldValue('Preferred_Program_Level', e?.label)}
                onBlur={() => formik.setFieldTouched('Preferred_Program_Level', true)}
                styles={{
                  control: (baseStyles: any) => ({
                    ...baseStyles,
                    height: '100%',
                    width: '100%',
                    borderRadius: '8px',
                    border: 'none',
                  }),
                }}
              />
              {formik.touched.Preferred_Program_Level && formik.errors.Preferred_Program_Level ? (
                <div className="text-danger">{formik.errors.Preferred_Program_Level}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Preferred_Intake_Year" className={`form-label ${styles.formLabel}`}>
                Which year do you want to start your studies? *
              </label>
              <Select
                isSearchable
                classNamePrefix="react-select"
                className={`${styles.formSelect} p-0`}
                name="Preferred_Intake_Year"
                options={preferredIntakeYearOptions}
                onChange={(e) => formik.setFieldValue('Preferred_Intake_Year', e?.label)}
                onBlur={() => formik.setFieldTouched('Preferred_Intake_Year', true)}
                styles={{
                  control: (baseStyles: any) => ({
                    ...baseStyles,
                    height: '100%',
                    width: '100%',
                    borderRadius: '8px',
                    border: 'none',
                  }),
                }}
              />
              {formik.touched.Preferred_Intake_Year && formik.errors.Preferred_Intake_Year ? (
                <div className="text-danger">{formik.errors.Preferred_Intake_Year}</div>
              ) : null}
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                value=""
                id="privacyPolicy"
                onChange={(e) => formik.setFieldValue('Term_Policy', !formik.values.Term_Policy)}
                onBlur={() => formik.setFieldTouched('Term_Policy', true)}
              />
              <label className={`form-check-label ${styles.formLabel}`} htmlFor="privacyPolicy">
                I agree to the{' '}
                <Link to={'https://blog.unifyapply.com/privacy-policy/'} target="_blank">
                  Privacy Policy
                </Link>
              </label>
              {formik.touched.Term_Policy && formik.errors.Term_Policy ? (
                <div className="text-danger">{formik.errors.Term_Policy}</div>
              ) : null}
            </div>
            <button
              className={`btn btn-primary ${styles.submitButton}`}
              type="submit"
              disabled={isLoading}
            >
              {landing_cmp.config.button_text}
            </button>
            <div className={styles.privacyNotes}>
              <span>
                Note: It is our responsibility to protect your privacy and we guarantee that your
                data will be completely confidential.
              </span>
            </div>
          </form>
        </div>
        {typeof landing_cmp.image_file === 'string' && (
          <div className="col-12 col-md-6 order-md-2 order-1">
            <img className="img-fluid" src={landing_cmp.image_file} />
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralFormPreview;
